import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { vehicleApi } from '../../lib/api';

const STATUS_MAP: Record<string, { label: string; color: string; bg: string; emoji: string }> = {
  available:       { label: '보관 중',   color: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-200', emoji: '🟢' },
  rented:          { label: '대여 중',   color: 'text-red-700',     bg: 'bg-red-50 border-red-200',       emoji: '🔴' },
  reserved:        { label: '예약 있음', color: 'text-amber-700',   bg: 'bg-amber-50 border-amber-200',   emoji: '🟡' },
  pending_review:  { label: '승인 대기', color: 'text-purple-700',  bg: 'bg-purple-50 border-purple-200', emoji: '⚪' },
  approved:        { label: '보관 중',   color: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-200', emoji: '🟢' },
  rejected:        { label: '승인 거절', color: 'text-gray-500',    bg: 'bg-gray-50 border-gray-200',     emoji: '🚫' },
  suspended:       { label: '일시 중지', color: 'text-gray-500',    bg: 'bg-gray-50 border-gray-200',     emoji: '⏸️' },
  draft:           { label: '작성 중',   color: 'text-slate-500',   bg: 'bg-slate-50 border-slate-200',   emoji: '📝' },
};

export default function MyVehiclesPage() {
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    vehicleApi.hostVehicles()
      .then((res: any) => { setVehicles(res.vehicles); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const formatPrice = (won: number) => new Intl.NumberFormat('ko-KR').format(won) + '원';

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">내 차량</h1>
          <p className="text-sm text-gray-500 mt-1">총 {vehicles.length}대 등록</p>
        </div>
        <Link
          to="/host/vehicles/new"
          className="flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white px-5 py-2.5 rounded-xl text-sm font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-brand-500"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          차량 등록
        </Link>
      </div>

      {vehicles.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-12 text-center">
          <svg className="w-16 h-16 text-gray-200 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
          <p className="text-gray-400 mb-2">아직 등록된 차량이 없습니다</p>
          <Link to="/host/vehicles/new" className="text-brand-600 hover:underline text-sm font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-brand-500 rounded">
            첫 차량 등록하기
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {vehicles.map((v: any) => {
            const status = STATUS_MAP[v.current_status] || STATUS_MAP.draft;
            const photoUrl = v.primary_photo;

            return (
              <div
                key={v.id}
                className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all overflow-hidden group"
              >
                {/* Photo */}
                <div className="aspect-[16/10] bg-gray-100 relative overflow-hidden">
                  {photoUrl ? (
                    <img
                      src={photoUrl}
                      alt={`${v.make} ${v.model}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-300 text-5xl">
                      🚗
                    </div>
                  )}
                  {/* Status badge */}
                  <div className="absolute top-3 left-3">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${status.bg} ${status.color}`}>
                      {status.emoji} {status.label}
                    </span>
                  </div>
                  {/* Vehicle type badge */}
                  <div className="absolute top-3 right-3">
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-white/80 backdrop-blur-sm text-gray-600">
                      {v.category === 'prop_car' ? '촬영용' : '렌트카'}
                    </span>
                  </div>
                </div>

                {/* Info */}
                <div className="p-4">
                  <h3 className="font-semibold text-slate-900 mb-0.5">{v.make} {v.model}</h3>
                  <p className="text-sm text-gray-400 mb-3">
                    {v.year}년식 · {v.color || '색상 미기재'} · {v.location_city}
                  </p>

                  {/* Specs row */}
                  <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
                    {v.license_plate && (
                      <span className="px-2 py-0.5 bg-gray-100 rounded-md">{v.license_plate}</span>
                    )}
                    <span>{formatPrice(v.daily_rate)}/일</span>
                  </div>

                  {/* Next rental info */}
                  {v.current_status === 'reserved' && v.next_rental_date && (
                    <p className="text-xs text-amber-600 mb-3">
                      다음 예약: {v.next_rental_date}
                    </p>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2">
                    <Link
                      to={`/host/vehicles/${v.id}/manage`}
                      className="flex-1 text-center py-2 rounded-lg text-sm font-medium bg-slate-50 hover:bg-slate-100 text-slate-700 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-brand-500"
                    >
                      관리
                    </Link>
                    <Link
                      to={`/host/calendar`}
                      className="flex-1 text-center py-2 rounded-lg text-sm font-medium bg-slate-50 hover:bg-slate-100 text-slate-700 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-brand-500"
                    >
                      일정
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
